import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { bold, num } from "../../utils/xlsx";
import { AsOfFilter, download, EmptyState, ExportButton, LoadingOverlay, money, ratio, REPORTS, sheetHeading, signTone, StatCard, todayIso } from "./common";
import { blockRows, sideBySideRows, StatementSide, StatementSides } from "./Statement";

/** Current assets against current liabilities on a date, with working capital and liquidity ratios. */
const WorkingCapital = () => {
  const [asOfDate, setAsOfDate] = useState(todayIso);
  const [detailed, setDetailed] = useState(true);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!asOfDate) {
      toast.error("Select a date");
      return;
    }
    setLoading(true);
    try {
      setReport(await api.get(`${REPORTS}/working-capital`, { asOfDate }));
    } catch (error) {
      toast.error(error.message || "Failed to load working capital");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rowsOf = (blocks, isDetailed) => blocks.flatMap((block) => (isDetailed ? blockRows(block) : blockRows({ ...block, lines: [] })));
  const sides = (isDetailed) => ({
    assets: { title: "Current Assets", rows: rowsOf(report.currentAssets, isDetailed), total: report.totalCurrentAssets },
    liabilities: { title: "Current Liabilities", rows: rowsOf(report.currentLiabilities, isDetailed), total: report.totalCurrentLiabilities },
  });

  const exportExcel = () => {
    const s = sides(true);
    const rows = [
      ...sheetHeading("Current Assets & Current Liabilities", `As on ${report.asOfDate}`),
      ...sideBySideRows(s.assets, s.liabilities),
      [],
      [bold("Working Capital"), bold(num(report.workingCapital))],
      ["Current Ratio", num(report.currentRatio)],
      ["Quick Ratio", num(report.quickRatio)],
    ];
    download(`Working Capital ${report.asOfDate}`, [{ name: "Working Capital", rows, widths: [40, 16, 4, 40, 16] }]);
  };

  const current = report ? sides(detailed) : null;

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Current Assets = cash + bank + receivables + closing stock + deposits and advances. Current Liabilities = payables + duties & taxes +
        provisions + bank OD/CC. Working Capital = Current Assets − Current Liabilities.
      </div>
      <div className="filters-bar">
        <AsOfFilter value={asOfDate} onChange={setAsOfDate} />
        <select value={detailed ? "detailed" : "summary"} onChange={(e) => setDetailed(e.target.value === "detailed")} aria-label="Detail level">
          <option value="detailed">Ledger-wise detail</option>
          <option value="summary">Group summary</option>
        </select>
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Run Report"}
        </button>
        <ExportButton onClick={exportExcel} disabled={!report} />
      </div>

      {!report ? (
        <EmptyState>{loading ? "Loading..." : "No data loaded"}</EmptyState>
      ) : (
        <>
          <div className="stat-grid" style={{ marginTop: "16px" }}>
            <StatCard label="Current Assets" value={`₹${money(report.totalCurrentAssets)}`} />
            <StatCard label="Current Liabilities" value={`₹${money(report.totalCurrentLiabilities)}`} />
            <StatCard label="Working Capital" value={`₹${money(report.workingCapital)}`} tone={signTone(report.workingCapital)} />
            <StatCard label="Current Ratio" value={ratio(report.currentRatio)} hint="Current Assets ÷ Current Liabilities" />
            <StatCard label="Quick Ratio" value={ratio(report.quickRatio)} hint="(Current Assets − Stock) ÷ Current Liabilities" />
          </div>
          <StatementSides>
            <StatementSide {...current.assets} />
            <StatementSide {...current.liabilities} />
          </StatementSides>
        </>
      )}
    </div>
  );
};

export default WorkingCapital;
